import { dayjs, formatDate, toDateString } from '@koob/dates';
import { SEARCH_TYPES } from '@koob/search';
import { roomsCompositionToFilters } from '@koob/utils';

export const filterByKind = (items, kind) => {
  return items?.reduce((acc, currentItem) => {
    if (currentItem.kind === kind) {
      acc.push(currentItem.id);
    }
    return acc;
  }, []);
};

/** The departure date shifted to the selected day, or the trip's own. */
function getEffectiveDepartureDate(tripFilters, selectedDay) {
  const tripDepartureDate = tripFilters?.departureDate;

  return tripDepartureDate != null && selectedDay != null
    ? dayjs(tripDepartureDate).add(selectedDay, 'days').toDate()
    : tripDepartureDate;
}

const getDmcId = tripFilters =>
  (tripFilters?.dmcId?.value ?? tripFilters?.dmcId)
    ? Number(tripFilters?.dmcId?.value ?? tripFilters?.dmcId)
    : undefined;

export function buildExperienceFilters({ tripFilters, selectedDay }) {
  const maxDuration = tripFilters?.expDuration?.max ?? 50;
  const location = tripFilters?.location ?? [];
  const cities = filterByKind(location, SEARCH_TYPES.CITY);
  const countries = filterByKind(location, SEARCH_TYPES.COUNTRY);
  const regions = filterByKind(location, SEARCH_TYPES.REGION);

  return {
    cities: cities,
    countries:
      location?.[0]?.kind === SEARCH_TYPES.COUNTRY
        ? countries
        : tripFilters?.countries?.map(country => country.value),
    regions: regions,
    dmcId: getDmcId(tripFilters),
    includePartnerExperiences: true,
    tripDuration: (tripFilters?.tripDuration ?? 1) - (selectedDay ?? 0),
    minDuration: tripFilters?.expDuration?.min ?? 0,
    maxDuration: maxDuration,
    departureDate: getEffectiveDepartureDate(tripFilters, selectedDay),
    minPax: tripFilters?.adultPax,
    maxPax:
      tripFilters?.adultPax + tripFilters?.childrenBirthdates?.length ?? 0,
    experiences: filterByKind(location, SEARCH_TYPES.PROGRAM),
    departureLocation:
      tripFilters?.departureLocation?.map(city => city.id) ?? [],
    returnLocation: tripFilters?.returnLocation?.map(city => city.id) ?? [],
    guided: tripFilters?.guided,
    themes: (tripFilters?.themes || []).map(({ id }) => id),
    segments: (tripFilters?.segments || []).map(({ id }) => id),
    templateId: tripFilters?.templateId,
    startingPoint: tripFilters?.startingPoint,
    endingPoint: tripFilters?.endingPoint,
    priceRange: tripFilters?.priceRange,
    travelersCount: tripFilters?.composition?.adults,
    experienceComposition: tripFilters?.composition,
    hasTemplatePrice: true,
    transitionProgramsOnly: tripFilters?.transitionProgramsOnly,
  };
}

/** The countries the hotel search covers: the searched ones, else the selected day's. */
function getHotelCountryIds({
  tripFilters,
  selectedDay,
  days,
  experiencesMap,
}) {
  const currentlySelectedDayExperienceId = days?.[selectedDay]?.experienceId;
  const currentlySelectedDayExperience = currentlySelectedDayExperienceId
    ? experiencesMap?.[currentlySelectedDayExperienceId]
    : null;
  const currentlySelectedDayCountryId =
    currentlySelectedDayExperience?.inCity?.country?.id;
  const filterCountryIds =
    tripFilters?.countries?.map(country => country.value) ?? [];

  if (tripFilters?.location?.[0]?.kind === SEARCH_TYPES.COUNTRY) {
    return [
      ...new Set([
        ...(tripFilters?.location ?? []).map(country => country.id),
        ...filterCountryIds,
      ]),
    ];
  }
  if (currentlySelectedDayCountryId) {
    return [...new Set([currentlySelectedDayCountryId, ...filterCountryIds])];
  }

  return [...new Set(filterCountryIds)];
}

export function buildHotelFilters({
  tripFilters,
  selectedDay,
  days,
  experiencesMap,
  tripId,
}) {
  const location = tripFilters?.location ?? [];
  const effectiveDepartureDate = getEffectiveDepartureDate(
    tripFilters,
    selectedDay,
  );
  const cityIds =
    tripFilters?.location?.[0]?.kind === SEARCH_TYPES.CITY
      ? tripFilters?.location?.map(city => city.id)
      : [];
  const regionIds = tripFilters?.location?.map(region => region.id);
  // Each room carries its own children's birthdates (KD-3758): read raw, a
  // trip split into rooms of 1 adult + 1 child searched hotels for a room of 3.
  const roomsComposition = roomsCompositionToFilters(tripFilters?.composition);

  return {
    cities: cityIds,
    countries:
      getHotelCountryIds({ tripFilters, selectedDay, days, experiencesMap }) ??
      [],
    hotels: filterByKind(location, SEARCH_TYPES.HOTEL),
    kinds: (tripFilters?.kinds ?? []).map(({ id }) => id),
    categories: (tripFilters?.categories ?? []).map(({ id }) => id),
    regions:
      tripFilters?.location?.[0]?.kind === SEARCH_TYPES.REGION
        ? (regionIds ?? [])
        : [],
    dmcId: getDmcId(tripFilters),
    includePartnerHotels: true,
    // The API scopes the list to the trip's DMC and its partners (KD-3825).
    tripId,
    styles: (tripFilters?.styles ?? []).map(({ id }) => id),
    sustainability: tripFilters.sustainableLevel?.id ?? null,
    stars: tripFilters?.stars,
    startAt: effectiveDepartureDate
      ? toDateString(effectiveDepartureDate)
      : undefined,
    endAt: effectiveDepartureDate
      ? formatDate(dayjs(effectiveDepartureDate).add(1, 'day'), 'YYYY-MM-DD')
      : undefined,
    rooms: roomsComposition,
    bypassLocationRequirement: true,
  };
}
